import React, { useEffect, useRef } from 'react';

/**
 * Inline text that becomes contentEditable when `editable` is true.
 * Commits on blur so the flyer stays responsive while typing.
 */
export default function EditableText({
  value = '',
  onChange,
  editable = false,
  as: Tag = 'span',
  className = '',
  style,
  multiline = false,
  'aria-label': ariaLabel,
}) {
  const elRef = useRef(null);

  // contentEditable nodes can keep stale computed styles; force color/font updates.
  useEffect(() => {
    const el = elRef.current;
    if (!el || !style) return;
    if (style.color != null) el.style.setProperty('color', String(style.color), 'important');
    if (style.fontSize != null) el.style.fontSize = String(style.fontSize);
    if (style.fontFamily != null) el.style.fontFamily = String(style.fontFamily);
  }, [style?.color, style?.fontSize, style?.fontFamily, style]);

  if (!editable) {
    return (
      <Tag ref={elRef} className={className} style={style}>
        {value}
      </Tag>
    );
  }

  return (
    <Tag
      ref={elRef}
      className={`ftl-editable ${className}`.trim()}
      style={style}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={ariaLabel || 'Edit text'}
      aria-multiline={multiline || undefined}
      spellCheck={false}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (!multiline && e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
        e.stopPropagation();
      }}
      onBlur={(e) => {
        const next = (e.currentTarget.textContent || '').replace(/\u00a0/g, ' ').trim();
        if (next !== (value || '').trim()) {
          onChange?.(next);
        } else {
          e.currentTarget.textContent = value ?? '';
        }
      }}
    >
      {value}
    </Tag>
  );
}
